import React, { useState } from 'react';
import { X, ArrowLeft, CheckCircle2, LogIn } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const { login, resetPassword, resendVerification } = useAuth();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const close = () => {
    onClose();
    setMode('login');
    setError('');
    setNotice('');
    setFormData({ email: '', password: '' });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setBusy(true);
    try {
      await login(formData.email, formData.password);
      close();
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Check your email and password.');
    } finally {
      setBusy(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setBusy(true);
    try {
      await resetPassword(formData.email);
      setNotice('Reset link sent. Check your inbox and spam folder.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the reset email.');
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setError('');
    setNotice('');
    setBusy(true);
    try {
      await resendVerification(formData.email);
      setNotice('Confirmation email sent. Check your inbox, then sign in.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend the confirmation email.');
    } finally {
      setBusy(false);
    }
  };

  const needsConfirmation = error.toLowerCase().includes('email not confirmed');

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto">
      <div className="flex min-h-full items-center justify-center px-4 py-10">
        <div className="fixed inset-0 bg-slate-950/55 backdrop-blur-sm" aria-hidden="true" onClick={close} />
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-title"
          className="relative w-full max-w-[440px] overflow-hidden rounded-3xl border border-rule bg-white shadow-[0_35px_100px_-35px_rgba(15,40,90,0.55)]"
        >
          <div className="flex items-center justify-between border-b border-rule px-5 py-4 sm:px-6">
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <LogIn className="h-4 w-4" strokeWidth={1.8} />
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-ink-2">LSEM Learning</span>
            </div>
            <button
              onClick={close}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-2 transition-colors hover:bg-blue-50 hover:text-blue-700"
              aria-label="Close"
            >
              <X className="h-4 w-4" strokeWidth={1.8} />
            </button>
          </div>

          <div className="px-5 pb-7 pt-6 sm:px-7">
            <h2 id="login-title" className="text-[24px] font-semibold leading-tight tracking-[-0.03em] text-ink">
              {mode === 'login' ? 'Welcome back.' : 'Recover your account.'}
            </h2>
            <p className="mt-2 text-[13px] leading-5 text-ink-2">
              {mode === 'login'
                ? 'Sign in to pick up your lessons, assessments and certificate record.'
                : 'Enter the email on your student account and we’ll send a secure reset link.'}
            </p>

            {error && (
              <div className="mt-4 rounded-xl border border-danger/20 bg-danger-soft px-3.5 py-3 text-[12.5px] leading-5 text-danger">
                <p>{error}</p>
                {needsConfirmation && (
                  <button type="button" onClick={resend} disabled={busy} className="mt-1.5 font-semibold underline">
                    Resend confirmation email
                  </button>
                )}
              </div>
            )}
            {notice && (
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-success/20 bg-success-soft px-3.5 py-3 text-[12.5px] leading-5 text-success">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={1.7} />
                <span>{notice}</span>
              </div>
            )}

            {mode === 'login' ? (
              <form onSubmit={handleLogin} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="login-email" className="lms-label">Email</label>
                  <input
                    id="login-email"
                    type="email"
                    required
                    autoComplete="email"
                    className="lms-field"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <div className="flex items-baseline justify-between">
                    <label htmlFor="login-password" className="lms-label">Password</label>
                    <button
                      type="button"
                      onClick={() => { setMode('forgot'); setError(''); setNotice(''); }}
                      className="mb-1.5 font-mono text-[9px] font-medium uppercase tracking-[0.1em] text-blue-700 underline decoration-blue-300 underline-offset-4 hover:decoration-blue-600"
                    >
                      Forgot password
                    </button>
                  </div>
                  <input
                    id="login-password"
                    type="password"
                    required
                    autoComplete="current-password"
                    className="lms-field"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                </div>
                <button type="submit" disabled={busy} className="lms-btn-primary w-full">
                  {busy ? 'Signing in…' : 'Sign in'}
                </button>
                <p className="text-center text-[12.5px] text-ink-2">
                  New to LSEM?{' '}
                  <button
                    type="button"
                    onClick={() => { close(); navigate('/signup'); }}
                    className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-4 hover:decoration-blue-600"
                  >
                    Create your student account
                  </button>
                </p>
              </form>
            ) : (
              <form onSubmit={handleForgot} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="reset-email" className="lms-label">Email</label>
                  <input
                    id="reset-email"
                    type="email"
                    required
                    autoComplete="email"
                    className="lms-field"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <button type="submit" disabled={busy} className="lms-btn-primary w-full">
                  {busy ? 'Sending…' : 'Send reset link'}
                </button>
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(''); setNotice(''); }}
                  className="flex w-full items-center justify-center gap-1.5 text-[12.5px] font-medium text-ink-2 hover:text-blue-700"
                >
                  <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.7} /> Back to sign in
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
